import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useCreateLead } from '@saiens/shared/hooks';
import { useAuthStore, toast } from '@saiens/shared/stores';
import { getUsers } from '@saiens/shared/api';
import type { CrmLeadCreateRequest } from '@saiens/shared/types';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ArrowLeft, Search, Check } from 'lucide-react';

const INPUT_CLASS =
  'min-h-11 w-full rounded-lg border border-gray-300 bg-white px-3 text-base text-gray-900 outline-none focus:border-primary';

/**
 * 建立 CRM 案件。
 *
 * 「負責業務」預設就是自己（不送 user_id，後端維持原本的建立者邏輯），
 * 需要代打時才展開選單指定他人——過去代打的案子會永遠掛在代打人名下，
 * 之後任何介面都改不回來。
 */
export function CrmCreatePage() {
  const navigate = useNavigate();
  const employee = useAuthStore((state) => state.employee);
  const createLead = useCreateLead();

  const [name, setName] = useState('');
  const [contactName, setContactName] = useState('');
  const [phone, setPhone] = useState('');
  const [projectAddress, setProjectAddress] = useState('');

  const [pickingOwner, setPickingOwner] = useState(false);
  const [ownerSearch, setOwnerSearch] = useState('');
  const [owner, setOwner] = useState<{ id: number; name: string } | null>(null);

  const {
    data: salesUsers,
    isLoading: loadingUsers,
    isError: usersFailed,
    error: usersError,
  } = useQuery({
    queryKey: ['master', 'users', 'sales', ownerSearch],
    queryFn: async () => {
      const result = await getUsers({ group: 'sales', search: ownerSearch });
      // 不得把失敗吞成空陣列——使用者會分不清「沒有業務」和「系統壞掉」。
      if (!result.success) throw new Error(result.message || '無法載入業務清單');
      return result.data ?? [];
    },
    enabled: pickingOwner,
  });

  const canSubmit = name.trim().length > 0 && !createLead.isPending;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;

    const payload: CrmLeadCreateRequest = { name: name.trim() };
    if (contactName.trim()) payload.contact_name = contactName.trim();
    if (phone.trim()) payload.phone = phone.trim();
    if (projectAddress.trim()) payload.project_address = projectAddress.trim();
    // 只有指定他人時才送 user_id；不送＝維持後端既有的「建立者即負責業務」。
    if (owner) payload.user_id = owner.id;

    try {
      const lead = await createLead.mutateAsync(payload);
      toast.success('案件建立成功');
      navigate(`/crm/${lead.id}`, { replace: true });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '建立失敗，請稍後再試');
    }
  };

  return (
    <div className="px-4 py-6">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="mb-4 flex items-center gap-1 text-sm text-gray-500"
      >
        <ArrowLeft className="h-4 w-4" />
        返回
      </button>

      <h1 className="mb-4 text-xl font-bold text-gray-900">新增案件</h1>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="rounded-xl bg-white p-4 shadow-sm">
          <label className="block">
            <span className="text-sm font-medium text-gray-700">案名 *</span>
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              className={`mt-1.5 ${INPUT_CLASS}`}
              placeholder="例：新竹明湖路 81 號朱宅案"
              required
            />
          </label>
        </div>

        {/* 負責業務：預設是自己，需要代打時才展開 */}
        <div className="rounded-xl bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-700">負責業務</p>
              <p className="mt-0.5 truncate text-base text-gray-900">
                {owner ? owner.name : `我（${employee?.name ?? '目前使用者'}）`}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setPickingOwner((open) => !open)}
              className="shrink-0 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700"
            >
              {pickingOwner ? '收合' : '更換'}
            </button>
          </div>

          {owner && (
            <p className="mt-3 rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-800">
              此案將掛在 {owner.name} 名下，你會被記錄為建立者。
            </p>
          )}

          {pickingOwner && (
            <div className="mt-3 border-t border-gray-100 pt-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  value={ownerSearch}
                  onChange={(event) => setOwnerSearch(event.target.value)}
                  className={`${INPUT_CLASS} pl-9`}
                  placeholder="搜尋業務姓名"
                />
              </div>

              {loadingUsers ? (
                <div className="py-4">
                  <LoadingSpinner />
                </div>
              ) : usersFailed ? (
                <p className="mt-2 bg-red-50 px-3 py-2 text-sm font-medium text-red-600">
                  {usersError instanceof Error ? usersError.message : '無法載入業務清單'}
                </p>
              ) : (
                <ul className="mt-2 max-h-64 overflow-y-auto">
                  <li>
                    <button
                      type="button"
                      onClick={() => {
                        setOwner(null);
                        setPickingOwner(false);
                      }}
                      className="flex min-h-11 w-full items-center justify-between px-1 text-left text-base text-gray-900"
                    >
                      我（{employee?.name ?? '目前使用者'}）
                      {!owner && <Check className="h-4 w-4 text-primary" />}
                    </button>
                  </li>
                  {(salesUsers ?? []).map((user) => (
                    <li key={user.id}>
                      <button
                        type="button"
                        onClick={() => {
                          setOwner({ id: user.id, name: user.name });
                          setPickingOwner(false);
                        }}
                        className="flex min-h-11 w-full items-center justify-between px-1 text-left text-base text-gray-900"
                      >
                        {user.name}
                        {owner?.id === user.id && <Check className="h-4 w-4 text-primary" />}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <div className="space-y-3 rounded-xl bg-white p-4 shadow-sm">
          <label className="block">
            <span className="text-sm font-medium text-gray-700">聯絡人</span>
            <input
              value={contactName}
              onChange={(event) => setContactName(event.target.value)}
              className={`mt-1.5 ${INPUT_CLASS}`}
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-gray-700">聯絡電話</span>
            <input
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              className={`mt-1.5 ${INPUT_CLASS}`}
              inputMode="tel"
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-gray-700">工地地址</span>
            <input
              value={projectAddress}
              onChange={(event) => setProjectAddress(event.target.value)}
              className={`mt-1.5 ${INPUT_CLASS}`}
            />
          </label>
        </div>

        <button
          type="submit"
          disabled={!canSubmit}
          className="min-h-11 w-full rounded-lg bg-primary px-4 text-base font-semibold text-white disabled:opacity-50"
        >
          {createLead.isPending ? '建立中…' : '建立案件'}
        </button>
      </form>
    </div>
  );
}
